/**
 * Feature Flag Editor Dialog — creation only.
 * Editing of an existing flag is handled inline by OverviewPanel in FlagDetail.
 */

import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Switch } from '@/components/ui/Switch';
import { useI18n } from '@/i18n';
import type { FlagType, CreateFlagRequest } from '@/types/featureFlags';

interface FlagEditorProps {
  existingKeys: string[];
  onSave: (data: CreateFlagRequest) => void;
  onClose: () => void;
  isSubmitting?: boolean;
}

const FLAG_TYPES: FlagType[] = ['deployment', 'release', 'isolation', 'ops'];

export function FlagEditor({ existingKeys, onSave, onClose, isSubmitting }: FlagEditorProps) {
  const { t } = useI18n();

  const [key, setKey] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [flagType, setFlagType] = useState<FlagType>('release');
  const [defaultValue, setDefaultValue] = useState(false);
  const [rollout, setRollout] = useState(0);
  const [description, setDescription] = useState('');
  const [owner, setOwner] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!key.trim()) {
      newErrors.key = t('featureFlags.flagEditor.flagNameError');
    } else if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(key)) {
      newErrors.key = t('featureFlags.flagEditor.flagNameFormatError');
    } else if (existingKeys.includes(key)) {
      newErrors.key = t('featureFlags.flagEditor.flagNameExistsError');
    }
    if (description.length > 500) {
      newErrors.description = t('featureFlags.flagEditor.descriptionLengthError');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      flag_key: key,
      flag_type: flagType,
      default_value: defaultValue,
      rollout_percentage: rollout,
      display_name: displayName.trim() || undefined,
      description: description.trim() || undefined,
      owner: owner.trim() || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            {t('featureFlags.flagEditor.createTitle')}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Flag Key */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('featureFlags.flagEditor.flagName')}
            </label>
            <input
              type="text"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder={t('featureFlags.flagEditor.flagNamePlaceholder')}
              className={`
                w-full px-3 py-2 border rounded-lg font-mono text-sm bg-white
                ${errors.key ? 'border-red-500' : 'border-gray-300'}
                focus:outline-none focus:ring-2 focus:ring-primary-500
              `}
            />
            {errors.key && <p className="mt-1 text-sm text-red-600">{errors.key}</p>}
          </div>

          {/* Display Name (中文名) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('featureFlags.flagEditor.displayName')}
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={100}
              placeholder={t('featureFlags.flagEditor.displayNamePlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Flag Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('featureFlags.flagEditor.flagType')}
            </label>
            <select
              value={flagType}
              onChange={(e) => setFlagType(e.target.value as FlagType)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {FLAG_TYPES.map((ft) => (
                <option key={ft} value={ft}>{ft}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('featureFlags.flagEditor.description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('featureFlags.flagEditor.descriptionPlaceholder')}
              rows={2}
              className={`
                w-full px-3 py-2 border rounded-lg text-sm
                ${errors.description ? 'border-red-500' : 'border-gray-300'}
                focus:outline-none focus:ring-2 focus:ring-primary-500
              `}
            />
            <div className="flex justify-between mt-1">
              {errors.description ? <p className="text-sm text-red-600">{errors.description}</p> : <span />}
              <span className="text-xs text-gray-400">{description.length}/500</span>
            </div>
          </div>

          {/* Owner */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('featureFlags.flagEditor.owner')}
            </label>
            <input
              type="text"
              value={owner}
              onChange={(e) => setOwner(e.target.value)}
              placeholder={t('featureFlags.flagEditor.ownerPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {/* Default Value */}
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium text-gray-700">
              {t('featureFlags.flagEditor.enabledByDefault')}
            </label>
            <Switch
              checked={defaultValue}
              onChange={setDefaultValue}
              ariaLabel={t('featureFlags.flagEditor.enabledByDefault')}
            />
          </div>

          {/* Rollout percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('featureFlags.flagEditor.rolloutPercentage')}
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={0}
                max={100}
                value={rollout}
                onChange={(e) => setRollout(Number(e.target.value))}
                className="flex-1"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={rollout}
                  onChange={(e) => setRollout(Math.min(100, Math.max(0, Number(e.target.value))))}
                  className="w-16 px-2 py-1 border border-gray-300 rounded text-sm text-center focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
                <span className="text-sm text-gray-500">%</span>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
            <Button type="button" variant="secondary" onClick={onClose} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="primary" loading={isSubmitting}>
              {t('featureFlags.flagEditor.createTitle')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
